import React, { useState } from 'react';
import { Printer, FileText, X } from 'lucide-react';
import ReceiptModal from './ReceiptModal';
import { printReceiptHTML } from './ReceiptPrint';

interface ReceiptItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

interface Transaction {
  id?: number;
  timestamp?: string;
  items: ReceiptItem[];
  subtotal: number;
  tax: number;
  total: number;
  tax_rate?: number;
  final_total?: number;
  discount_amount?: number;
  coupon_discount?: number;
  manual_discount?: number;
  payment_type: 'cash' | 'paypay';
  received?: number;
  change?: number;
  order_type: 'eatin' | 'takeout';
}

interface ReceiptPreviewProps {
  transaction: Transaction;
  onPrint: () => void;
  onSavePDF: () => void;
  onClose: () => void;
  isOpen: boolean;
}

const ReceiptPreview: React.FC<ReceiptPreviewProps> = ({
  transaction,
  onPrint,
  onSavePDF,
  onClose,
  isOpen
}) => {
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  
  if (!isOpen) return null;

  const formatDateTime = (timestamp?: string) => {
    const date = timestamp ? new Date(timestamp) : new Date();
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number | undefined) => {
    if (amount === undefined || amount === null) {
      return '¥0';
    }
    return `¥${amount.toLocaleString()}`;
  };

  const displayTaxRate = (() => {
    if (typeof transaction.tax_rate === 'number' && transaction.tax_rate > 0) {
      return transaction.tax_rate;
    }
    const subtotalNum = Number(transaction.subtotal || 0);
    const taxNum = Number(transaction.tax || 0);
    if (subtotalNum > 0 && Number.isFinite(subtotalNum) && Number.isFinite(taxNum)) {
      const calculatedRate = Math.round((taxNum / subtotalNum) * 100);
      if (calculatedRate > 0 && calculatedRate <= 100) {
        return calculatedRate;
      }
    }
    return transaction.order_type === 'takeout' ? 8 : 10;
  })();

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-semibold">レシートプレビュー</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {/* レシート本体 */}
          <div className="bg-white border-2 border-gray-300 p-6 font-mono text-sm">
            {/* ヘッダー */}
            <div className="text-center mb-4">
              <h3 className="text-xl font-bold mb-1">とんかつ和とん</h3>
              <p className="text-xs">三重県鈴鹿市白子駅前通り</p>
              <p className="text-xs">TEL: 059-XXX-XXXX</p>
            </div>

            <div className="border-t border-dashed border-gray-400 my-3"></div>

            {/* 日時・レシート番号 */}
            <div className="mb-3">
              <p className="text-xs">{formatDateTime(transaction.timestamp)}</p>
              <p className="text-xs">
                レシート番号: {transaction.id?.toString().padStart(8, '0') || '00000000'}
              </p>
              <p className="text-xs">
                {transaction.order_type === 'eatin' ? '店内' : 'テイクアウト'}
              </p>
            </div>

            <div className="border-t border-dashed border-gray-400 my-3"></div>

            {/* 商品明細 */}
            <div className="mb-3">
              {(transaction.items || []).map((item, index) => (
                <div key={index} className="flex justify-between mb-1">
                  <div className="flex-1">
                    <span className="text-xs">{item.name}</span>
                    <span className="text-xs ml-2">x{item.quantity}</span>
                  </div>
                  <span className="text-xs">{formatCurrency((item.price || 0) * (item.quantity || 0))}</span>
                </div>
              ))}
            </div>

            <div className="border-t border-gray-400 my-3"></div>

            {/* 小計・税・合計 */}
            <div className="mb-3">
              <div className="flex justify-between text-xs mb-1">
                <span>小計</span>
                <span>{formatCurrency(transaction.subtotal)}</span>
              </div>
              <div className="flex justify-between text-xs mb-1">
                <span>消費税({displayTaxRate}%)</span>
                <span>{formatCurrency(transaction.tax)}</span>
              </div>
              {transaction.discount_amount && transaction.discount_amount > 0 && (
                <div className="flex justify-between text-xs mb-1 text-red-600 font-semibold">
                  <span>値引き{transaction.coupon_discount && transaction.coupon_discount > 0 ? '（クーポン）' : transaction.manual_discount && transaction.manual_discount > 0 ? '（店舗）' : ''}</span>
                  <span>-{formatCurrency(transaction.discount_amount)}</span>
                </div>
              )}
            </div>

            <div className="border-t-2 border-gray-400 my-3"></div>

            <div className="flex justify-between font-bold text-base mb-3">
              <span>{transaction.discount_amount && transaction.discount_amount > 0 ? 'お支払い額' : '合計'}</span>
              <span>{formatCurrency(transaction.final_total || transaction.total)}</span>
            </div>

            {/* 支払い情報 */}
            <div className="border-t border-gray-400 my-3"></div>

            <div className="mb-3">
              <div className="flex justify-between text-xs mb-1">
                <span>お支払い方法</span>
                <span>{transaction.payment_type === 'cash' ? '現金' : 'PayPay'}</span>
              </div>
              {transaction.payment_type === 'cash' && transaction.received && (
                <>
                  <div className="flex justify-between text-xs mb-1">
                    <span>お預かり</span>
                    <span>{formatCurrency(transaction.received)}</span>
                  </div>
                  <div className="flex justify-between text-xs mb-1">
                    <span>お釣り</span>
                    <span>{formatCurrency(transaction.change || 0)}</span>
                  </div>
                </>
              )}
            </div>

            <div className="border-t border-dashed border-gray-400 my-3"></div>

            {/* フッター */}
            <div className="text-center text-xs">
              <p>ありがとうございました</p>
              <p>またのご来店をお待ちしております</p>
            </div>
          </div>
        </div>

        {/* アクションボタン */}
        <div className="p-4 border-t flex gap-3">
          <button
            onClick={() => {
              console.log('領収書ボタンクリック');
              setShowReceiptModal(true);
            }}
            className="flex-1 flex items-center justify-center gap-2 bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 transition-colors"
          >
            <FileText className="h-5 w-5" />
            <span>領収書</span>
          </button>
          <button
            onClick={async () => {
              console.log('Print button clicked - Using HTML printing');
              await printReceiptHTML(transaction);
              onClose();
            }}
            className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Printer className="h-5 w-5" />
            <span>印刷</span>
          </button>
        </div>
      </div>
    </div>
    
    {/* 領収書モーダル */}
    <ReceiptModal
      transaction={transaction}
      onClose={() => setShowReceiptModal(false)}
      isOpen={showReceiptModal}
    />
    </>
  );
};

export default ReceiptPreview;
